import React, { useState, useEffect } from "react";
import { Spinner } from "@/components/shared/LoadingSystem";
import "./Section.css";
import ImageUploader from "./ImageUploader";
import QRScanner from "./QRScanner";
import fileService from "@shared/api/filesService";
import { EMPRESA_FEATURES } from "@shared/config/testConfig";

// Función para convertir texto a Camel Case
const toCamelCase = (str) => {
  if (!str) return str;
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const ChecklistSection = ({
  seccion,
  updateItemResponse,
  renderCase,
  reportInfo,
  globalValidatedQRMachines,
  onQRValidation,
  onToggleMachineInSection,
}) => {
  useEffect(() => {
    if (seccion && seccion.maquinas) {
      // console.log(
      //   "[DEBUG] Máquinas recibidas en ChecklistSection:",
      //   seccion.maquinas,
      // );
    }
  }, [seccion]);
  const sortedItems =
    seccion && seccion.items
      ? [...seccion.items].sort(
          (a, b) => (a.orden || 0) - (b.orden || 0) || a.id - b.id,
        )
      : [];
  const headers = sortedItems.map((item) => item);
  const columnToAttributeMap = {
    "#": "numero",
    Ubicación: "ubicacion",
    Tipo: "tipo",
    "Írea Protegida": "areaprotegida",
    Módulo: "modulo",
    Diámetro: "diametro",
    "Medida Nominal": "medidanominal",
    "Matriz Causa y Efecto": "matrizcausaefecto",
    Descripción: "descripcion",
    Descripcion: "descripcion", // Manejar sin tilde también
  };

  const [selectedValues, setSelectedValues] = useState({});
  const [headerSelections, setHeaderSelections] = useState({});
  const [machineSelections, setMachineSelections] = useState({});
  // Estados para manejo de QR - usar el estado global
  const validatedQRMachines = globalValidatedQRMachines || new Set();
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [currentMachineForQR, setCurrentMachineForQR] = useState(null);
  const allMachines = Array.isArray(seccion?.maquinas) ? seccion.maquinas : [];
  const activeMachines = allMachines.filter(
    (maquina) => maquina.activaEnReporte !== false,
  );
  const machineTypesWithSelectiveToggle = new Set([6, 7, 17]);
  const shouldShowMachineToggle =
    renderCase === "create" &&
    allMachines.length > 0 &&
    (machineTypesWithSelectiveToggle.has(Number(seccion?.idtipomaquina)) ||
      allMachines.some((maquina) =>
        machineTypesWithSelectiveToggle.has(Number(maquina?.idtipomaquina)),
      ));

  // Determinar si se debe usar el layout alternativo (items en filas)
  const shouldUseRowLayout = () => {
    const numItems = sortedItems.length;
    const machineTypesForRowLayout = new Set([6, 7, 17]);
    const sectionMachineType = Number(seccion?.idtipomaquina);

    const hasRowLayoutMachineType =
      machineTypesForRowLayout.has(sectionMachineType) ||
      (seccion?.maquinas || []).some((maquina) =>
        machineTypesForRowLayout.has(Number(maquina?.idtipomaquina)),
      );

    // Para modo previsualización (newFormat), usar layout de filas si hay más de 8 items y el tipo de máquina aplica
    if (renderCase === "newFormat") {
      const useRow = hasRowLayoutMachineType && numItems > 8;
      // console.log(
      //   `[DEBUG] ChecklistSection (newFormat) - Sección: ${seccion.nombreseccion}`,
      // );
      // console.log(
      //   `[DEBUG] Tipo máquina válido: ${hasRowLayoutMachineType}, Número de items: ${numItems}`,
      // );
      // console.log(
      //   `[DEBUG] ¿Usar layout de filas?: ${useRow} (tipo máquina en [6,7,17] && items > 8)`,
      // );
      return useRow;
    }

    // Para otros casos, usar layout de filas solo si el tipo de máquina aplica y hay más de 8 items
    const useRow = hasRowLayoutMachineType && numItems > 8;

    // Debug log para verificar la lógica
    // console.log(`[DEBUG] ChecklistSection - Sección: ${seccion.nombreseccion}`);
    // console.log(
    //   `[DEBUG] Tipo máquina válido: ${hasRowLayoutMachineType}, Número de items: ${numItems}`,
    // );
    // console.log(
    //   `[DEBUG] ¿Usar layout de filas?: ${useRow} (tipo máquina en [6,7,17] && items > 8)`,
    // );

    return useRow;
  };

  const useRowLayout = shouldUseRowLayout();

  useEffect(() => {
    if (renderCase === "newFormat") return;

    const initialValues = {};
    if (seccion.maquinas && seccion.maquinas.length > 0) {
      seccion.maquinas.forEach((maquina) => {
        if (maquina.items && maquina.items.length > 0) {
          maquina.items.forEach((item) => {
            const key = `${maquina.idmaquina}-${item.id}`;
            initialValues[key] = item.respuesta;
          });
        }
      });
      // console.log("[DEBUG] Valores iniciales cargados:", initialValues);
      setSelectedValues(initialValues);
    }
  }, [seccion, renderCase]);

  // Funciones para manejo de QR
  const handleQRScanRequest = (maquina) => {
    setCurrentMachineForQR(maquina);
    setShowQRScanner(true);
  };

  const handleQRScanSuccess = (validationResult) => {
    if (validationResult.valido && validationResult.maquina) {
      // Verificar si es una máquina marcada como inaccesible
      if (validationResult.esInaccesible) {
        // console.log(
        //   "Procesando máquina inaccesible:",
        //   validationResult.maquina,
        // );
        handleInaccessibleMachine(validationResult.maquina);
      }

      // Usar la función global para actualizar el estado
      if (onQRValidation) {
        // Usar idmaquina que es el campo correcto
        const machineId =
          validationResult.maquina.idmaquina || validationResult.maquina.id;
        onQRValidation(
          machineId,
          true,
          validationResult.esInaccesible || false,
        );
      }
      setShowQRScanner(false);
      setCurrentMachineForQR(null);

      // Mostrar mensaje de éxito
      // console.log(
      //   `QR ${
      //     validationResult.esInaccesible
      //       ? "marcado como inaccesible"
      //       : "validado"
      //   } para máquina ${validationResult.maquina.numero}`,
      // );
    }
  };

  const handleQRScanClose = () => {
    setShowQRScanner(false);
    setCurrentMachineForQR(null);
  };

  const handleInaccessibleMachine = (maquina) => {
    // console.log("Procesando máquina inaccesible:", maquina);

    // Obtener todos los items de tipo booleano para esta máquina y marcarlos como N/A
    if (seccion.maquinas && seccion.maquinas.length > 0) {
      const targetMaquina = seccion.maquinas.find(
        (m) => m.idmaquina === maquina.idmaquina || m.idmaquina === maquina.id,
      );

      if (targetMaquina) {
        // Procesar items booleanos
        targetMaquina.items.forEach((item) => {
          if (item.idtiporespuesta === 1) {
            // Item booleano
            // console.log(
            //   `Estableciendo N/A para item ${item.id} de máquina inaccesible`,
            // );
            handleIndividualChange(targetMaquina.idmaquina, item.id, null); // null = N/A
          }
        });
      }
    }
  };

  const isMachineQRValidated = (maquinaId) => {
    // En modo view siempre permitir (reportes ya completados)
    if (renderCase === "view" || renderCase === "newFormat") {
      return true;
    }

    // ðŸ”¥ BYPASS: Si es empresa de prueba, NO requiere validación de QR
    const skipQRValidation = EMPRESA_FEATURES.skipQRValidation(
      reportInfo?.idempresacliente,
    );
    if (skipQRValidation) {
      return true;
    }

    return validatedQRMachines.has(maquinaId);
  };

  const handleSelectAll = (header, value) => {
    // console.log(
    //   `[DEBUG] SELECT ALL para columna ${header.descripcion} con valor: ${value}`,
    // );

    const newSelectedValues = { ...selectedValues };
    const newHeaderSelections = { ...headerSelections, [header.id]: value };

    let itemsAfectados = [];
    let itemsIgnorados = [];

    if (activeMachines.length > 0) {
      activeMachines.forEach((maquina) => {
        if (maquina.items && maquina.items.length > 0) {
          maquina.items.forEach((item) => {
            if (
              item.id === header.id &&
              item.idtipoitem === 1 &&
              item.idtiporespuesta === 1
            ) {
              // Verificar si el item tiene precarga
              if (item.tienePrecarga) {
                itemsIgnorados.push({
                  maquinaId: maquina.idmaquina,
                  itemId: item.id,
                  razon: "Tiene valor precargado",
                  valorPrecargado: item.respuesta,
                });
                return; // Salir del forEach para este item sin modificarlo
              }

              const key = `${maquina.idmaquina}-${item.id}`;
              newSelectedValues[key] = value;

              itemsAfectados.push({
                maquinaId: maquina.idmaquina,
                itemId: item.id,
                valorAnterior: selectedValues[key],
                nuevoValor: value,
              });

              if (updateItemResponse) {
                updateItemResponse(
                  seccion.idseccionreporte,
                  item.id,
                  item.idrespuestaitem,
                  item.idrespuestabooleana,
                  item.idrespuestanumerica,
                  item.idrespuestatexto,
                  item.idrespuestaimagen,
                  value,
                  maquina.idmaquina,
                );
              }
            }
          });
        }
      });
    }

    // console.log("[DEBUG] Items afectados por SELECT ALL:", itemsAfectados);
    // console.log(
    //   "[DEBUG] Items ignorados por SELECT ALL (tienen precarga):",
    //   itemsIgnorados,
    // );
    // console.log("[DEBUG] Nuevo estado de selectedValues:", newSelectedValues);

    setSelectedValues(newSelectedValues);
    setHeaderSelections(newHeaderSelections);
  };

  const handleSelectAllByMachine = (maquina, value) => {
    if (!maquina?.items || maquina.items.length === 0) return;

    const newSelectedValues = { ...selectedValues };
    const newMachineSelections = {
      ...machineSelections,
      [maquina.idmaquina]: value,
    };

    maquina.items.forEach((item) => {
      if (item.idtipoitem === 1 && item.idtiporespuesta === 1) {
        if (item.tienePrecarga) return;

        const key = `${maquina.idmaquina}-${item.id}`;
        newSelectedValues[key] = value;

        if (updateItemResponse) {
          updateItemResponse(
            seccion.idseccionreporte,
            item.id,
            item.idrespuestaitem,
            item.idrespuestabooleana,
            item.idrespuestanumerica,
            item.idrespuestatexto,
            item.idrespuestaimagen,
            value,
            maquina.idmaquina,
          );
        }
      }
    });

    setSelectedValues(newSelectedValues);
    setMachineSelections(newMachineSelections);
  };

  const handleToggleMachineInReport = (maquina) => {
    if (!onToggleMachineInSection) return;
    const isCurrentlyActive = maquina.activaEnReporte !== false;

    if (isCurrentlyActive) {
      const machineLabel = `# ${maquina.numero || "N/A"} - ${
        maquina.nombretipo || maquina.tipo || "Sin tipo"
      }`;
      const shouldExclude = window.confirm(
        `¿Deseas excluir ${machineLabel} de este reporte?\n\nNo se guardarán sus respuestas ni se requerirá su QR.`,
      );

      if (!shouldExclude) return;
    }

    onToggleMachineInSection(
      seccion.idseccionreporte,
      maquina.idmaquina,
      !isCurrentlyActive,
    );
  };

  const handleIndividualChange = (maquinaId, itemId, value) => {
    const key = `${maquinaId}-${itemId}`;

    // Log mejorado para depuración
    // console.log(
    //   `[DEBUG] CAMBIO INDIVIDUAL - Máquina: ${maquinaId}, Item: ${itemId}, Nuevo valor: ${value}`,
    // );

    // Actualizamos el estado local inmediatamente
    setSelectedValues((prev) => ({
      ...prev,
      [key]: value,
    }));

    const relevantItem =
      seccion.maquinas && seccion.maquinas.length > 0
        ? seccion.maquinas
            .find((m) => m.idmaquina === maquinaId)
            ?.items.find((i) => i.id === itemId)
        : null;

    if (relevantItem && headerSelections[relevantItem.id] !== undefined) {
      setHeaderSelections((prev) => ({
        ...prev,
        [relevantItem.id]: undefined,
      }));
    }

    if (machineSelections[maquinaId] !== undefined) {
      setMachineSelections((prev) => ({
        ...prev,
        [maquinaId]: undefined,
      }));
    }

    if (!seccion.maquinas) return;

    const maquina = seccion.maquinas.find((m) => m.idmaquina === maquinaId);
    if (!maquina || !maquina.items) return;

    const item = maquina.items.find((i) => i.id === itemId);
    if (!item) return;

    if (updateItemResponse) {
      // Forzamos la actualización inmediata en el componente padre
      // console.log(
      //   `[DEBUG] Actualizando respuesta en el padre, ítem ${item.id}: ${value}`,
      // );

      if (item.idtiporespuesta === 4 && typeof value === "object") {
        updateItemResponse(
          seccion.idseccionreporte,
          item.id,
          item.idrespuestaitem,
          item.idrespuestabooleana,
          item.idrespuestanumerica,
          item.idrespuestatexto,
          item.idrespuestaimagen,
          value.valorimagen,
          maquinaId,
          value.descripcion,
        );
      } else {
        updateItemResponse(
          seccion.idseccionreporte,
          item.id,
          item.idrespuestaitem,
          item.idrespuestabooleana,
          item.idrespuestanumerica,
          item.idrespuestatexto,
          item.idrespuestaimagen,
          value,
          maquinaId,
        );
      }

      // Verificar que la actualización se realizó correctamente
      setTimeout(() => {
        const updatedItem = seccion.maquinas
          .find((m) => m.idmaquina === maquinaId)
          ?.items.find((i) => i.id === itemId);

        // console.log(
        //   `[DEBUG] Después de actualizar, ítem ${itemId} tiene respuesta: ${updatedItem?.respuesta}`,
        // );
      }, 0);
    }
  };

  useEffect(() => {
    // console.log("ChecklistSection reportInfo:", reportInfo);
    // console.log(
    //   "¿atributosMaquina está presente?",
    //   !!reportInfo?.atributosMaquina,
    // );
    // console.log("Contenido de atributosMaquina:", reportInfo?.atributosMaquina);
  }, [reportInfo]);

  // Función para ordenar máquinas por su campo "numero":
  // 1. Primero los que tienen número puramente numérico (ordenados numéricamente)
  // 2. Luego los alfanuméricos (mezcla de letras y números, ordenados naturalmente)
  // 3. Al final los que no tienen número o son solo texto
  const sortMaquinasByNumero = (maquinas) => {
    return [...maquinas].sort((a, b) => {
      const numA = a.numero != null ? String(a.numero).trim() : "";
      const numB = b.numero != null ? String(b.numero).trim() : "";

      const isNumericA = numA !== "" && /^\d+$/.test(numA);
      const isNumericB = numB !== "" && /^\d+$/.test(numB);
      const hasDigitA = numA !== "" && /\d/.test(numA);
      const hasDigitB = numB !== "" && /\d/.test(numB);

      // Categorías: 0 = numérico puro, 1 = alfanumérico, 2 = sin número / solo texto
      const catA = isNumericA ? 0 : hasDigitA ? 1 : 2;
      const catB = isNumericB ? 0 : hasDigitB ? 1 : 2;

      if (catA !== catB) return catA - catB;

      // Dentro de la misma categoría
      if (catA === 0) {
        // Ambos son numéricos puros: comparar como números
        return parseInt(numA, 10) - parseInt(numB, 10);
      }
      if (catA === 1) {
        // Ambos son alfanuméricos: ordenamiento natural (localeCompare con numeric)
        return numA.localeCompare(numB, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }
      // Ambos sin número o solo texto: ordenar alfabéticamente
      return numA.localeCompare(numB, undefined, { sensitivity: "base" });
    });
  };

  // Función para renderizar la tabla normal (items en columnas) agrupando por ubicación
  const renderNormalTable = () => {
    if (activeMachines.length === 0) return null;

    // Agrupar máquinas por ubicación (ignorando mayúsculas/minúsculas)
    const maquinasPorUbicacion = activeMachines.reduce((acc, maquina) => {
      // Convertir a mayúsculas para evitar discrepancias
      const ubicacionRaw = maquina.ubicacion || "Sin ubicación";
      const ubicacion = ubicacionRaw.toUpperCase();
      if (!acc[ubicacion]) acc[ubicacion] = [];
      acc[ubicacion].push(maquina);
      return acc;
    }, {});

    // Ordenar las máquinas dentro de cada grupo de ubicación
    Object.keys(maquinasPorUbicacion).forEach((ubicacion) => {
      maquinasPorUbicacion[ubicacion] = sortMaquinasByNumero(
        maquinasPorUbicacion[ubicacion],
      );
    });

    // Renderizar
    return (
      <table className="table-report">
        <thead>
          <tr className="table-row-report">
            {/* Columna QR (solo en modo create) */}
            {renderCase === "create" && (
              <th className="th-report qr-header">
                <i className="fas fa-qrcode"></i>
                <span>QR</span>
              </th>
            )}

            {headers.map((header, index) => (
              <th className="th-report" key={index}>
                {header.descripcion}
                {renderCase === "create" &&
                  header.idtipoitem === 1 &&
                  header.idtiporespuesta === 1 &&
                  !useRowLayout && (
                    <div className="checkbox-container">
                      <label>
                        <input
                          type="checkbox"
                          checked={headerSelections[header.id] === true}
                          onChange={() => handleSelectAll(header, true)}
                        />{" "}
                        OK
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={headerSelections[header.id] === false}
                          onChange={() => handleSelectAll(header, false)}
                        />{" "}
                        N/G
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={headerSelections[header.id] === null}
                          onChange={() => handleSelectAll(header, null)}
                        />{" "}
                        N/A
                      </label>
                    </div>
                  )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.entries(maquinasPorUbicacion).map(([ubicacion, maquinas]) => (
            <React.Fragment key={ubicacion}>
              {/* Fila de sección de ubicación */}
              <tr className="table-row-report ubicacion-row">
                <td
                  className="td-report ubicacion-label"
                  colSpan={headers.length + (renderCase === "create" ? 1 : 0)}
                >
                  {ubicacion}
                </td>
              </tr>
              {/* Filas de máquinas de esa ubicación */}
              {maquinas.map((maquina) => {
                const isQRValidated = isMachineQRValidated(maquina.idmaquina);

                return (
                  <tr
                    className={`table-row-report ${
                      !isQRValidated ? "qr-not-validated" : ""
                    }`}
                    key={maquina.idmaquina}
                  >
                    {/* Columna QR (solo en modo create) */}
                    {renderCase === "create" && (
                      <td className="td-report qr-column">
                        {!isQRValidated ? (
                          <button
                            className="qr-scan-button"
                            onClick={() => handleQRScanRequest(maquina)}
                            title="Escanear QR para validar máquina"
                          >
                            <i className="fas fa-qrcode"></i>
                            <span>Escanear QR</span>
                          </button>
                        ) : (
                          <div className="qr-validated">
                            <i className="fas fa-check-circle"></i>
                            <span>Validado</span>
                          </div>
                        )}
                      </td>
                    )}

                    {maquina.items &&
                      [...maquina.items]
                        .sort(
                          (a, b) =>
                            (a.orden || 0) - (b.orden || 0) || a.id - b.id,
                        )
                        .map((item, index) => {
                          const key = `${maquina.idmaquina}-${item.id}`;
                          const itemValue = selectedValues[key];

                          // Si la columna es "Ubicación", mostrar ubicacionespecifica
                          const headerObj = headers[index];
                          if (
                            headerObj &&
                            (headerObj.descripcion === "Ubicación" ||
                              headerObj.descripcion === "Ubicacion")
                          ) {
                            // Concatenar ubicacion y ubicacionespecifica
                            let valorUbicacion = "";
                            if (maquina.ubicacionespecifica) {
                              valorUbicacion = maquina.ubicacionespecifica;
                            } else {
                              valorUbicacion = "-";
                            }
                            return (
                              <td className="td-report" key={index}>
                                {valorUbicacion}
                              </td>
                            );
                          }

                          return (
                            <td className="td-report" key={index}>
                              <ItemInput
                                item={item}
                                maquina={maquina}
                                columnToAttributeMap={columnToAttributeMap}
                                header={item.descripcion}
                                selectedValue={
                                  itemValue !== undefined
                                    ? itemValue
                                    : item.respuesta
                                }
                                onChange={(value) =>
                                  handleIndividualChange(
                                    maquina.idmaquina,
                                    item.id,
                                    value,
                                  )
                                }
                                renderCase={renderCase}
                                reportInfo={reportInfo}
                                isQRValidated={isQRValidated}
                              />
                            </td>
                          );
                        })}
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    );
  };

  // Función para renderizar la tabla alternativa (items en filas)
  const renderRowTable = () => {
    const maquinasOrdenadas = sortMaquinasByNumero(activeMachines);

    return (
      <div className="row-layout-container">
        <table className="table-report row-layout">
          <thead>
            <tr className="table-row-report">
              <th className="th-report">Item</th>
              {maquinasOrdenadas.map((maquina) => {
                const isQRValidated = isMachineQRValidated(maquina.idmaquina);
                return (
                  <th className="th-report" key={maquina.idmaquina}>
                    <div>
                      <strong># {maquina.numero || "N/A"}</strong>
                    </div>
                    <div>{maquina.tipo || "Sin tipo"}</div>
                    <div>{maquina.ubicacionespecifica || maquina.ubicacion || "Sin ubicación"}</div>
                    {renderCase !== "view" && renderCase !== "newFormat" && (
                      <div className="qr-scan-controls" style={{ marginTop: "0.4rem" }}>
                        {!isQRValidated ? (
                          <button
                            className="qr-scan-button"
                            onClick={() => handleQRScanRequest(maquina)}
                            title="Escanear QR del dispositivo"
                          >
                            <i className="fa-solid fa-qrcode"></i>
                            Escanear QR
                          </button>
                        ) : (
                          <div className="qr-validated">
                            <i className="fa-solid fa-check-circle"></i>
                            QR Validado
                          </div>
                        )}
                        <div className="checkbox-container" style={{ marginTop: "0.35rem" }}>
                          <label>
                            <input
                              type="checkbox"
                              checked={machineSelections[maquina.idmaquina] === true}
                              onChange={() => handleSelectAllByMachine(maquina, true)}
                            />{" "}
                            OK
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={machineSelections[maquina.idmaquina] === false}
                              onChange={() => handleSelectAllByMachine(maquina, false)}
                            />{" "}
                            N/G
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={machineSelections[maquina.idmaquina] === null}
                              onChange={() => handleSelectAllByMachine(maquina, null)}
                            />{" "}
                            N/A
                          </label>
                        </div>
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {headers.map((header, headerIndex) => {
              return (
                <tr className="table-row-report" key={headerIndex}>
                  <td className="td-report item-header">
                    {header.descripcion}
                    {header.esPersonalizada && (
                      <span
                        className="item-personalizada-badge"
                        title="Este ítem está personalizado para esta planta"
                      >
                        <i className="fa-solid fa-cog"></i>
                      </span>
                    )}
                  </td>
                  {maquinasOrdenadas.map((maquina) => {
                    const item =
                      maquina.items &&
                      maquina.items.find((i) => i.id === header.id);

                    if (!item) {
                      return (
                        <td className="td-report" key={`${maquina.idmaquina}-${header.id}`}>
                          <span>-</span>
                        </td>
                      );
                    }

                    const key = `${maquina.idmaquina}-${item.id}`;
                    const itemValue = selectedValues[key];
                    const isItemQRValidated = isMachineQRValidated(maquina.idmaquina);

                    return (
                      <td
                        className={`td-report ${!isItemQRValidated ? "qr-not-validated" : ""}`}
                        key={`${maquina.idmaquina}-${header.id}`}
                      >
                        <div
                          className={`input-cell-container ${
                            !isItemQRValidated ? "qr-disabled" : ""
                          }`}
                        >
                          <ItemInput
                            item={item}
                            maquina={maquina}
                            columnToAttributeMap={columnToAttributeMap}
                            header={item.descripcion}
                            selectedValue={
                              itemValue !== undefined
                                ? itemValue
                                : item.respuesta
                            }
                            onChange={(value) =>
                              handleIndividualChange(
                                maquina.idmaquina,
                                item.id,
                                value,
                              )
                            }
                            renderCase={renderCase}
                            reportInfo={reportInfo}
                            isQRValidated={isItemQRValidated}
                          />
                          {!isItemQRValidated &&
                            renderCase !== "view" &&
                            renderCase !== "newFormat" && (
                              <div className="qr-required-overlay">
                                <i className="fa-solid fa-qrcode"></i>
                                <span>QR Requerido</span>
                              </div>
                            )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (renderCase === "newFormat") {
    return (
      <div className="section">
        <p className="section-title">{seccion.nombreseccion}</p>
        <p>
          Codigos Empleados: OK - Correcto, NG - No Garantizada, NA - No Aplica
        </p>
        <div className="report-table-container">
          {useRowLayout ? (
            <table className="table-report row-layout">
              <thead>
                <tr className="table-row-report">
                  <th className="th-report">Item</th>
                  <th className="th-report">Respuesta</th>
                </tr>
              </thead>
              <tbody>
                {headers.map((header, index) => (
                  <tr className="table-row-report" key={index}>
                    <td className="td-report item-header">
                      {header.descripcion}
                    </td>
                    <td className="td-report">
                      <div className="new-format">
                        <label>Respuesta</label>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="table-report">
              <thead>
                <tr className="table-row-report">
                  {headers.map((header, index) => (
                    <th className="th-report" key={index}>
                      {header.descripcion}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="table-row-report">
                  {headers.map((item, index) => (
                    <td className="td-report" key={index}>
                      <div className="new-format">
                        <label>Respuesta</label>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="section">
      <p className="section-title">{seccion.nombreseccion}</p>
      {seccion.idtipomaquina && (
        <div className="section-tipo-maquina-tag">
          <span>
            <i className="fa-solid fa-tag"></i>
            {(() => {
              const tipoMaquina = reportInfo?.tiposMaquina?.find(
                (t) => t.idtipomaquina === seccion.idtipomaquina,
              );
              return tipoMaquina
                ? tipoMaquina.nombretipo
                : `Tipo ${seccion.idtipomaquina}`;
            })()}
          </span>
        </div>
      )}
      <p>
        Codigos Empleados: OK - Correcto, NG - No Garantizada, NA - No Aplica
      </p>

      {shouldShowMachineToggle && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            marginBottom: "0.8rem",
          }}
        >
          {allMachines
            .slice()
            .sort((a, b) =>
              String(a.numero || "").localeCompare(String(b.numero || ""), undefined, {
                numeric: true,
                sensitivity: "base",
              }),
            )
            .map((maquina) => {
              const isActive = maquina.activaEnReporte !== false;
              return (
                <button
                  key={maquina.idmaquina}
                  type="button"
                  onClick={() => handleToggleMachineInReport(maquina)}
                  style={{
                    border: "1px solid",
                    borderColor: isActive ? "#2e7d32" : "#8d6e63",
                    background: isActive ? "rgba(46,125,50,0.2)" : "rgba(141,110,99,0.18)",
                    color: "#fff",
                    borderRadius: "999px",
                    padding: "0.3rem 0.7rem",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.35rem",
                  }}
                  title={
                    isActive
                      ? "Excluir máquina de este reporte"
                      : "Incluir máquina en este reporte"
                  }
                >
                  <i
                    className={`fa-solid ${
                      isActive ? "fa-toggle-on" : "fa-toggle-off"
                    }`}
                  ></i>
                  <span>
                    # {maquina.numero || "N/A"} - {maquina.nombretipo || maquina.tipo || "Sin tipo"}
                    {isActive ? " (incluida)" : " (excluida)"}
                  </span>
                </button>
              );
            })}
        </div>
      )}

      <div className="report-table-container">
        {activeMachines.length === 0 ? (
          <div
            style={{
              padding: "0.9rem",
              border: "1px dashed #888",
              borderRadius: "8px",
              fontSize: "0.92rem",
            }}
          >
            No hay máquinas incluidas en esta sección. Usa los toggles para incluir al menos una máquina.
          </div>
        ) : useRowLayout ? (
          renderRowTable()
        ) : (
          renderNormalTable()
        )}
      </div>

      {/* Image modal for previewing images */}
      <div id="imageModal" className="image-modal">
        <span
          className="close"
          onClick={() => {
            document.getElementById("imageModal").style.display = "none";
          }}
          style={{
            position: "absolute",
            top: "15px",
            right: "35px",
            color: "#f1f1f1",
            fontSize: "40px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          &times;
        </span>
        <img id="img01" className="modal-content" alt="Modal preview" />
        <div id="caption"></div>
      </div>

      {/* Modal de escáner QR */}
      <QRScanner
        isOpen={showQRScanner}
        onClose={handleQRScanClose}
        onScanSuccess={handleQRScanSuccess}
        maquinaInfo={currentMachineForQR}
      />
    </div>
  );
};

const ItemInput = ({
  item,
  maquina,
  columnToAttributeMap,
  header,
  selectedValue,
  onChange,
  renderCase,
  reportInfo,
  isQRValidated = true, // Default true para modo view y casos legacy
}) => {
  // Si es un item de tipo consulta y tiene un atributo de máquina asociado
  let value = "-";

  if (item.idtipoitem === 2) {
    // Caso especial para Ubicación: siempre mostrar ubicacionespecifica
    if (
      header?.descripcion === "Ubicación" ||
      header?.descripcion === "UBICACIÓN" ||
      header?.descripcion === "Ubicacion" ||
      item.descripcion === "Ubicación" ||
      item.descripcion === "UBICACIÓN" ||
      item.descripcion === "Ubicacion"
    ) {
      value = maquina?.ubicacionespecifica || maquina?.ubicacion || "-";
      // Caso especial para EQUIPO / UBICACIÓN: prioridad nombretipo (de idtipomaquina), fallback tipo (tipoespecifico)
    } else if (
      item.descripcion === "EQUIPO / UBICACIÓN" ||
      item.descripcion === "EQUIPO / UBICACION" ||
      item.descripcion === "Equipo / Ubicación" ||
      item.descripcion === "Equipo / Ubicacion"
    ) {
      const equipo = maquina?.nombretipo || maquina?.tipo || "";
      const ubic = maquina?.ubicacion || "";
      value = [equipo, ubic].filter(Boolean).join(" / ") || "-";
    } else if (
      item.idatributomaquina &&
      maquina &&
      reportInfo?.atributosMaquina
    ) {
      // Buscar el atributo por su ID
      const atributo = reportInfo?.atributosMaquina?.find(
        (attr) => attr.idatributomaquina === item.idatributomaquina,
      );

      if (
        atributo &&
        atributo.keyatributo &&
        maquina[atributo.keyatributo] !== undefined
      ) {
        // Usar el keyatributo para obtener el valor del campo correcto en la máquina
        value = maquina[atributo.keyatributo] || "-";
      } else {
        // Fallback al método antiguo basado en la descripción
        const attribute = columnToAttributeMap[header?.descripcion];
        value = maquina && attribute ? maquina[attribute] || "-" : "-";
      }
    } else {
      // Fallback al método antiguo si no hay idatributomaquina
      const attribute = columnToAttributeMap[header?.descripcion];
      value = maquina && attribute ? maquina[attribute] || "-" : "-";
    }
  }

  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchImageUrl = async (path) => {
      if (!path) return;

      setLoading(true);
      setImageError(false);

      try {
        const url = await fileService.getImage(path);
        if (isMounted) {
          setImageUrl(url);
        }
      } catch (err) {
        console.error("Error fetching image:", err);
        if (isMounted) {
          setImageError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    let imageData;

    if (selectedValue) {
      if (typeof selectedValue === "string") {
        // Solo intentar parsear si el string parece ser JSON (empieza con { o [)
        if (
          selectedValue.trim().startsWith("{") ||
          selectedValue.trim().startsWith("[")
        ) {
          try {
            imageData = JSON.parse(selectedValue);
          } catch (e) {
            console.error("Error parsing image data:", e);
            // Si falla el parsing, tratar como ruta directa
            imageData = { valorimagen: selectedValue };
          }
        }
        // Si no es JSON, asumimos que es una ruta directa de imagen
        else if (
          selectedValue !== "N/A" &&
          selectedValue !== "-" &&
          selectedValue.trim() !== ""
        ) {
          imageData = { valorimagen: selectedValue };
        }
      } else if (typeof selectedValue === "object" && selectedValue !== null) {
        imageData = selectedValue;
      }

      if (imageData && (imageData.path || imageData.valorimagen)) {
        fetchImageUrl(imageData.path || imageData.valorimagen);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [selectedValue]);

  useEffect(() => {
    if (maquina && item) {
      // console.log(
      //   `[DEBUG] ItemInput - Máquina: ${maquina.idmaquina}, Item: ${item.id}, Valor actual: ${selectedValue}`,
      // );
    }
  }, [selectedValue, maquina, item]);

  if (renderCase === "view") {
    if (item.idtipoitem === 1 && item.idtiporespuesta === 1) {
      if (selectedValue === true) {
        return <span>OK</span>;
      } else if (selectedValue === false) {
        return <span>N/G</span>;
      } else {
        return <span>N/A</span>;
      }
    } else if (item.idtipoitem === 1) {
      switch (item.idtiporespuesta) {
        case 2:
          return <span>{selectedValue || "-"}</span>;
        case 3:
          return <span>{toCamelCase(selectedValue) || "-"}</span>;
        case 4:
          try {
            let imageData;
            if (selectedValue) {
              if (typeof selectedValue === "string") {
                // Verificar si el string parece ser JSON (empieza con { o [)
                if (
                  selectedValue.trim().startsWith("{") ||
                  selectedValue.trim().startsWith("[")
                ) {
                  try {
                    imageData = JSON.parse(selectedValue);
                  } catch (parseError) {
                    console.error("Error parsing image JSON:", parseError);
                    // Si falla el parsing, tratar como ruta directa
                    imageData = {
                      valorimagen: selectedValue,
                      descripcion: item.descripcionImagen || "Sin descripción",
                    };
                  }
                } else {
                  // Si no parece JSON, tratarlo como ruta directa de imagen
                  imageData = {
                    valorimagen: selectedValue,
                    descripcion: item.descripcionImagen || "Sin descripción",
                  };
                }
              } else if (
                typeof selectedValue === "object" &&
                selectedValue !== null
              ) {
                imageData = selectedValue;
              }

              if (imageData && (imageData.path || imageData.valorimagen)) {
                const description =
                  imageData.description ||
                  imageData.descripcion ||
                  item.descripcionImagen ||
                  "Sin descripción";

                return (
                  <div className="existing-image-container">
                    {loading ? (
                      <div className="image-loading">
                        <Spinner size="small" />
                      </div>
                    ) : imageError ? (
                      <div className="image-error">
                        <i className="fa-solid fa-triangle-exclamation"></i>
                      </div>
                    ) : (
                      <img
                        src={imageUrl}
                        alt={description}
                        className="existing-image-preview"
                        onClick={() => {
                          const modal = document.getElementById("imageModal");
                          const modalImg = document.getElementById("img01");
                          const captionText =
                            document.getElementById("caption");
                          if (modal && modalImg && captionText) {
                            modal.style.display = "block";
                            modalImg.src = imageUrl;
                            captionText.innerHTML = description;
                          }
                        }}
                      />
                    )}
                    <p
                      className="existing-image-description"
                      title={description}
                    >
                      {description.length > 15
                        ? description.substring(0, 15) + "..."
                        : description}
                    </p>
                  </div>
                );
              }
            }

            return (
              <div className="no-image-container">
                <span className="no-image-text">Sin imagen</span>
              </div>
            );
          } catch (e) {
            console.error("Error al mostrar imagen existente:", e);
            return <span className="error-text">Error imagen</span>;
          }
        default:
          return <span>{toCamelCase(selectedValue) || "-"}</span>;
      }
    } else {
      return <span>{toCamelCase(value || selectedValue)}</span>;
    }
  } else if (renderCase === "create") {
    // Verificar si el item tiene un valor precargado (respuesta no nula y no vacía)
    const tienePrecarga = item.tienePrecarga || false;
    const observacionesPrecarga = item.observacionesPrecarga || null;

    // Debug log para verificar qué está pasando
    // console.log(
    //   `[DEBUG] Item ${item.id} - respuesta: "${item.respuesta}", tienePrecarga: ${tienePrecarga}, observacionesPrecarga: "${observacionesPrecarga}"`,
    // );

    if (item.idtipoitem === 1 && item.idtiporespuesta === 1) {
      // Si tiene respuesta precargada, mostrar como label
      if (tienePrecarga) {
        return (
          <div className="valor-precargado-container">
            <span
              className="valor-precargado-texto"
              title={
                observacionesPrecarga
                  ? `Valor precargado: ${observacionesPrecarga}`
                  : "Valor precargado para esta máquina"
              }
            >
              {item.respuesta}
            </span>
            <i
              className="fa-solid fa-lock valor-precargado-icono"
              title="Este valor está precargado y no se puede modificar"
            ></i>
          </div>
        );
      }

      // Si no tiene precarga, mostrar los checkboxes normales
      return (
        <div
          className={`checkbox-container ${
            !isQRValidated ? "qr-disabled" : ""
          }`}
        >
          {!isQRValidated && (
            <div className="qr-required-overlay">
              <i className="fas fa-qrcode"></i>
              <span>Escanea el QR primero</span>
            </div>
          )}
          <label>
            <input
              type="checkbox"
              checked={selectedValue === true}
              onChange={() => isQRValidated && onChange(true)}
              disabled={!isQRValidated}
            />{" "}
            OK
          </label>
          <label>
            <input
              type="checkbox"
              checked={selectedValue === false}
              onChange={() => isQRValidated && onChange(false)}
              disabled={!isQRValidated}
            />{" "}
            N/G
          </label>
          <label>
            <input
              type="checkbox"
              checked={selectedValue === null}
              onChange={() => isQRValidated && onChange(null)}
              disabled={!isQRValidated}
            />{" "}
            N/A
          </label>
        </div>
      );
    } else if (item.idtipoitem === 1) {
      // Para otros tipos de respuesta, también verificar si tienen respuesta precargada
      if (tienePrecarga) {
        return (
          <div className="valor-precargado-container">
            <span
              className="valor-precargado-texto"
              title="Valor precargado para esta máquina"
            >
              {item.respuesta}
            </span>
            <i
              className="fa-solid fa-lock valor-precargado-icono"
              title="Este valor está precargado y no se puede modificar"
            ></i>
          </div>
        );
      }

      switch (item.idtiporespuesta) {
        case 2:
          return (
            <div
              className={`input-container ${
                !isQRValidated ? "qr-disabled" : ""
              }`}
            >
              {!isQRValidated && (
                <div className="qr-required-overlay">
                  <i className="fas fa-qrcode"></i>
                  <span>Escanea el QR primero</span>
                </div>
              )}
              <input
                type="number"
                className="input-report"
                value={selectedValue || ""}
                onChange={(e) => isQRValidated && onChange(e.target.value)}
                disabled={!isQRValidated}
              />
            </div>
          );
        case 3:
          return (
            <div
              className={`input-container ${
                !isQRValidated ? "qr-disabled" : ""
              }`}
            >
              {!isQRValidated && (
                <div className="qr-required-overlay">
                  <i className="fas fa-qrcode"></i>
                  <span>Escanea el QR primero</span>
                </div>
              )}
              <input
                type="text"
                className="input-report"
                value={selectedValue || ""}
                onChange={(e) => isQRValidated && onChange(e.target.value)}
                disabled={!isQRValidated}
                onBlur={(e) => {
                  // Aplicar Camel Case al perder el foco
                  const camelCaseValue = toCamelCase(e.target.value);
                  if (camelCaseValue !== e.target.value) {
                    isQRValidated && onChange(camelCaseValue);
                  }
                }}
              />
            </div>
          );
        case 4:
          let existingImage = null;
          try {
            let imageData;
            if (selectedValue) {
              if (typeof selectedValue === "string") {
                // Verificar si el string parece ser JSON (empieza con { o [)
                if (
                  selectedValue.trim().startsWith("{") ||
                  selectedValue.trim().startsWith("[")
                ) {
                  try {
                    imageData = JSON.parse(selectedValue);
                  } catch (parseError) {
                    console.error("Error parsing image JSON:", parseError);
                    // Si falla el parsing, tratar como ruta directa
                    imageData = {
                      valorimagen: selectedValue,
                      descripcion: item.descripcionImagen || "Sin descripción",
                    };
                  }
                } else {
                  // Si no parece JSON, tratarlo como ruta directa de imagen
                  imageData = {
                    valorimagen: selectedValue,
                    descripcion: item.descripcionImagen || "Sin descripción",
                  };
                }
              } else if (
                typeof selectedValue === "object" &&
                selectedValue !== null
              ) {
                imageData = selectedValue;
              }

              if (imageData && (imageData.path || imageData.valorimagen)) {
                const description =
                  imageData.description ||
                  imageData.descripcion ||
                  item.descripcionImagen ||
                  "Sin descripción";

                existingImage = (
                  <div className="existing-image-container">
                    {loading ? (
                      <div className="image-loading">
                        <Spinner size="small" />
                      </div>
                    ) : imageError ? (
                      <div className="image-error">
                        <i className="fa-solid fa-triangle-exclamation"></i>
                      </div>
                    ) : (
                      <img
                        src={imageUrl}
                        alt={description}
                        className="existing-image-preview"
                        onClick={() => {
                          const modal = document.getElementById("imageModal");
                          const modalImg = document.getElementById("img01");
                          const captionText =
                            document.getElementById("caption");
                          if (modal && modalImg && captionText) {
                            modal.style.display = "block";
                            modalImg.src = imageUrl;
                            captionText.innerHTML = description;
                          }
                        }}
                      />
                    )}
                    <p
                      className="existing-image-description"
                      title={description}
                    >
                      {description.length > 15
                        ? description.substring(0, 15) + "..."
                        : description}
                    </p>
                    <button
                      className="remove-existing-image"
                      onClick={() => onChange(null)}
                      title="Eliminar esta imagen"
                    >
                      <i className="fa-solid fa-trash"></i>
                      <span className="remove-text">Eliminar</span>
                    </button>
                  </div>
                );
              }
            }
          } catch (e) {
            console.error("Error al mostrar imagen existente:", e);
          }

          return (
            <div className="image-input-container">
              {existingImage || (
                <ImageUploader
                  onImageUpload={(imagePath, description) => {
                    const imageData = {
                      valorimagen: imagePath,
                      descripcion: description,
                    };
                    onChange(imageData);
                  }}
                  idReporte={reportInfo?.idreporte || ""}
                  idItem={item.id}
                  idRespuestaImagen={item.idrespuestaimagen}
                  idMaquina={maquina?.idmaquina}
                />
              )}
            </div>
          );
        default:
          return <span>{selectedValue}</span>;
      }
    } else {
      return <label>{toCamelCase(value || selectedValue)}</label>;
    }
  } else if (renderCase === "newFormat") {
    if (item.idtipoitem === 1 && item.idtiporespuesta === 1) {
      return (
        <div className="new-format">
          <label>
            {item.descripcion}
            {item.esPersonalizada && (
              <span
                className="item-personalizada-badge"
                title="Este ítem está personalizado para esta planta"
              >
                <i className="fa-solid fa-cog"></i>
              </span>
            )}{" "}
            <input type="checkbox" checked={selectedValue === true} readOnly />{" "}
            {selectedValue === true ? "OK" : "N/G"}
          </label>
        </div>
      );
    } else if (item.idtipoitem === 1) {
      return (
        <div className="new-format">
          <label>
            {item.descripcion}
            {item.esPersonalizada && (
              <span
                className="item-personalizada-badge"
                title="Este ítem está personalizado para esta planta"
              >
                <i className="fa-solid fa-cog"></i>
              </span>
            )}
          </label>
        </div>
      );
    } else {
      return (
        <div className="new-format">
          <label>
            {value || item.descripcion}
            {item.esPersonalizada && (
              <span
                className="item-personalizada-badge"
                title="Este ítem está personalizado para esta planta"
              >
                <i className="fa-solid fa-cog"></i>
              </span>
            )}
          </label>
        </div>
      );
    }
  }
};

export default ChecklistSection;
